<template>
	<Disclosure v-slot="{ open }" as="div">
		<DisclosureButton :class="['w-full flex justify-between items-center', btnClass]" @click="handleClick">
			<div class="flex items-center gap-1.5">
				<span ref="scrollAnchor" :class="['!pe-2', titleStyle]">{{ title }}</span>
				<slot name="count" />
			</div>
			<template v-if="altIcon">
				<template v-if="useStore ? activeAccordion === indexKey : open">
					<svg-vue icon="accordion-minus" :class="['w-5 stroke-white shrink-0', iconStyle]" />
				</template>
				<template v-else>
					<svg-vue icon="accordion-plus" :class="['w-5 stroke-white shrink-0', iconStyle]" />
				</template>
			</template>
			<template v-else>
				<svg-vue
					icon="chevron-down"
					:class="[
						'w-5 stroke-white shrink-0',
						useStore ? activeAccordion === indexKey : open && 'transition-all duration-200 rotate-180 transform',
						iconStyle,
					]"
				/>
			</template>
		</DisclosureButton>
		<template v-if="useStore">
			<transition
				v-if="transition && activeAccordion === indexKey"
				enter-active-class="transition duration-100 ease-out"
				enter-from-class="transform scale-95 opacity-0"
				enter-to-class="transform scale-100 opacity-100"
				leave-active-class="transition duration-75 ease-out"
				leave-from-class="transform scale-100 opacity-100"
				leave-to-class="transform scale-95 opacity-0"
			>
				<DisclosurePanel :class="panelStyle">
					<slot name="content" />
				</DisclosurePanel>
			</transition>
			<template v-else>
				<div v-show="activeAccordion === indexKey">
					<DisclosurePanel :class="panelStyle" :unmount="unmount">
						<slot name="content" />
					</DisclosurePanel>
				</div>
			</template>
		</template>
		<template v-else>
			<transition
				v-if="transition"
				enter-active-class="transition duration-100 ease-out"
				enter-from-class="transform scale-95 opacity-0"
				enter-to-class="transform scale-100 opacity-100"
				leave-active-class="transition duration-75 ease-out"
				leave-from-class="transform scale-100 opacity-100"
				leave-to-class="transform scale-95 opacity-0"
			>
				<DisclosurePanel :class="panelStyle">
					<slot name="content" />
				</DisclosurePanel>
			</transition>
			<template v-else>
				<DisclosurePanel :class="panelStyle" :unmount="unmount">
					<slot name="content" />
				</DisclosurePanel>
			</template>
		</template>
	</Disclosure>
</template>
<script setup>
import { ref } from 'vue'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { storeToRefs } from 'pinia'

import { usePaletteBuilderStore, useAppStore } from '@stores'

const stores = {
	'palette-builder': usePaletteBuilderStore,
}

const props = defineProps({
	indexKey: { type: String, required: false },
	btnClass: { type: String, default: '' },
	title: { type: String, default: 'Title' },
	titleStyle: { type: String, default: '' },
	altIcon: { type: Boolean, default: false },
	iconStyle: { type: String, default: '' },
	panelStyle: { type: String, default: '' },
	transition: { type: Boolean, default: false },
	unmount: { type: Boolean, default: true },
	useStore: { type: Boolean, default: false },
	store: { type: String, default: 'palette-builder' },
	autoScroll: { type: Boolean, default: false },
})

const store = stores[props.store]()
const { activeAccordion } = storeToRefs(store)
const { setActiveAccordion } = store
const appStore = useAppStore()
const { isLargeMediaQuery } = storeToRefs(appStore)

const scrollAnchor = ref()

const handleClick = () => {
	if (props.useStore) {
		if (activeAccordion.value !== props.indexKey) {
			setActiveAccordion(props.indexKey)
			if (props.autoScroll && !isLargeMediaQuery.value && scrollAnchor.value) {
				scrollAnchor.value.scrollIntoView({ behavior: 'smooth' })
			}
		} else {
			setActiveAccordion(null) // none selected
		}
	}
}
</script>
